import {
    AsOfBadge,
    Badge,
    DateTime,
    Money,
    ObjectRef,
    PageHeader,
    StatusBadge,
    type ListColumnDef,
    type ListViewPreset,
} from '@bemor/web-kit/ui';
import { Link } from 'react-router-dom';

import type { RiskCase } from '@/api/types';
import {
    AdminListPage,
    adminListPrefetch,
    filterDefs,
    useAdminList,
} from '@/lib/lists';
import { ADMIN_STATUSES } from '@/lib/statuses';

const PATH = '/staff/risk/cases';
const PAGE_KEY = 'risk-reviews';

/**
 * BM-125 decisions 20 and 28: the open cases, due soonest first, so the late ones lead (the contract's default sort);
 * the server lists open cases when no state is named, so a view with no state is never offered.
 */
export const RISK_REVIEW_PRESETS: ListViewPreset[] = [
    { key: 'open', name: 'Open', filters: { state: ['open'] } },
    { key: 'late', name: 'Late', filters: { state: ['open'], late: ['true'] } },
];

export const riskReviewsPrefetch = adminListPrefetch(PAGE_KEY, PATH, {
    presets: RISK_REVIEW_PRESETS,
});

const caseFilters = filterDefs(PATH, {
    state: 'State',
    kind: 'Kind',
    range: 'Opened',
});

export const caseHref = (id: string) => `/risk/reviews/${id}`;

/** BM-125 slice 2: Risk → Review, the one queue of held payments waiting for a staff decision. */
export function RiskReviewsPage() {
    const columns: ListColumnDef<RiskCase>[] = [
        {
            key: 'id',
            label: 'Case',
            cell: (c) => (
                <Link
                    to={caseHref(c.id)}
                    className="font-mono text-xs hover:underline"
                    onClick={(e) => e.stopPropagation()}
                >
                    {c.id}
                </Link>
            ),
            csv: (c) => c.id,
            boardCard: true,
            locked: 'start',
            noTruncate: true,
        },
        {
            key: 'kind',
            label: 'Kind',
            csv: (c) => c.kind,
            cell: (c) => (
                <StatusBadge
                    status={c.kind}
                    domain={ADMIN_STATUSES.riskCaseKind}
                />
            ),
        },
        {
            key: 'merchant_id',
            aliases: ['merchant'],
            label: 'Merchant',
            csv: (c) => c.merchant_id,
            cell: (c) => <ObjectRef id={c.merchant_id} />,
        },
        {
            key: 'amount_cents',
            aliases: ['amount'],
            label: 'Amount',
            sortKey: 'amount_cents',
            csv: (c) => c.amount_cents,
            boardCard: true,
            cell: (c) => <Money cents={c.amount_cents} currency={c.currency} />,
            noTruncate: true,
        },
        {
            key: 'score',
            label: 'Score',
            csv: (c) => c.score ?? '',
            cell: (c) =>
                c.score === null || c.score === undefined
                    ? '—'
                    : String(c.score),
        },
        {
            key: 'sla_due_at',
            aliases: ['due'],
            label: 'Due',
            kind: 'datetime',
            sortKey: 'sla_due_at',
            csv: (c) => c.sla_due_at,
            cell: (c) => (
                <span className="flex flex-wrap items-center gap-2">
                    <DateTime value={c.sla_due_at} empty="—" />
                    {c.late ? <Badge variant="danger">Late</Badge> : null}
                </span>
            ),
            noTruncate: true,
        },
        {
            key: 'expires_at',
            aliases: ['expires'],
            label: 'Voids',
            kind: 'datetime',
            sortKey: 'expires_at',
            csv: (c) => c.expires_at,
            cell: (c) => <DateTime value={c.expires_at} empty="—" />,
            noTruncate: true,
        },
        {
            key: 'state',
            label: 'State',
            csv: (c) => c.state,
            cell: (c) => (
                <StatusBadge
                    status={c.state}
                    domain={ADMIN_STATUSES.riskCase}
                />
            ),
        },
    ];
    const { state, query } = useAdminList<RiskCase>(PAGE_KEY, PATH, {
        columns,
        filters: caseFilters,
        presets: RISK_REVIEW_PRESETS,
    });
    return (
        <div>
            <PageHeader
                title="Review"
                meta={
                    <span className="flex flex-col gap-1">
                        <span>
                            Held payments waiting for a decision. Late cases
                            come first; an undecided case is voided when it
                            expires.
                        </span>
                        <AsOfBadge asOf={query.freshness.asOf} />
                    </span>
                }
            />
            <AdminListPage
                noun="cases"
                state={state}
                query={query}
                rowKey={(c) => c.id}
                controls={{
                    state: { domain: ADMIN_STATUSES.riskCase },
                    kind: { domain: ADMIN_STATUSES.riskCaseKind },
                }}
            />
        </div>
    );
}
